import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router';
import streamService, { Stream } from '../services/streams';
import aiService, {
  AISearchResult,
  RadioQueryAnswer,
  RadioQueryItem,
  describeQueryAnswer,
} from '../services/aiService';
import { useAudioPlayer, Stream as PlayerStream } from '../contexts/AudioPlayerContext';
import { useAuthPrompt } from '../contexts/AuthPromptContext';
import { logger } from '../utils/logger';
import { normalizeGenre, getUniqueGenres, taggedGenre } from '../utils/genreNormalization';
import { hasQuerySyntax } from '../utils/querySyntax';
import './Streams.css';

/** Where a set track starts, as a clock: 6:40, or 1:02:05 past an hour. */
function formatStart(ms: number): string {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

const QUERY_FAILED = 'The query could not run. Try again in a moment.';

interface TopUser {
  username: string;
  displayName: string;
  profilePictureUrl?: string;
  streamCount: number;
}

const Streams: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sortParam = searchParams.get('sort');
  const [streams, setStreams] = useState<Stream[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'trending' | 'most-played' | 'top-creators'>(
    sortParam === 'most-played' ? 'most-played' : sortParam === 'top-creators' ? 'top-creators' : 'trending'
  );
  const [searchQuery, setSearchQuery] = useState('');

  // AI Search state
  const [aiSearchResults, setAiSearchResults] = useState<AISearchResult[]>([]);
  const [aiSearchLoading, setAiSearchLoading] = useState(false);
  const [isAiSearch, setIsAiSearch] = useState(false);

  // TEM-1529 — text with the grammar's syntax is answered by GET /ai/query
  // (the shared grammar over Radio's shows) instead of the AI search.
  const [queryAnswer, setQueryAnswer] = useState<RadioQueryAnswer | null>(null);
  const [queryFailed, setQueryFailed] = useState(false);
  const [isQuerySearch, setIsQuerySearch] = useState(false);

  // Audio player for play functionality
  const { playStream } = useAudioPlayer();
  const { requireAuth } = useAuthPrompt();

  // Convert Stream service type to Player stream type
  const toPlayerStream = (stream: Stream): PlayerStream => ({
    streamId: stream.streamId,
    name: stream.name,
    description: stream.description,
    audioUrl: stream.audioUrl,
    coverImage: stream.coverImage,
    uploaderUsername: stream.uploaderUsername,
    uploaderDisplayName: stream.uploaderDisplayName,
    waveformData: stream.waveformData,
  });

  // Handle play stream with auth check
  const handlePlayStream = (e: React.MouseEvent, stream: Stream) => {
    e.preventDefault();
    e.stopPropagation();
    if (!requireAuth('play', stream.name)) return;
    playStream(toPlayerStream(stream));
  };


  const loadStreams = useCallback(async () => {
    try {
      setLoading(true);
      let data: Stream[];

      if (sortBy === 'trending') {
        data = await streamService.getTrendingStreams(500);
      } else if (sortBy === 'most-played') {
        data = await streamService.getMostPlayedStreams(500);
      } else if (sortBy === 'top-creators') {
        // For top creators, load all streams to calculate top users
        data = await streamService.getAllStreams('active');
      } else {
        data = await streamService.getAllStreams('active');
      }

      setStreams(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load streams');
    } finally {
      setLoading(false);
    }
  }, [sortBy]);

  useEffect(() => {
    loadStreams();
  }, [loadStreams]);

  // The shared query grammar (TEM-1529). A failure says so: substring-matching
  // `artist:coltrane` against show names would only look like an answer.
  const handleQuerySearch = async () => {
    try {
      setAiSearchLoading(true);
      setIsAiSearch(true);
      setIsQuerySearch(true);
      setQueryFailed(false);
      setQueryAnswer(null);
      const answer = await aiService.query(searchQuery, 20);
      setQueryAnswer(answer);
      logger.info('[Streams] Query search completed', {
        query: answer.query,
        total: answer.total,
        notAvailable: answer.notAvailable,
      });
    } catch (err) {
      logger.error('[Streams] Query search failed', { error: err });
      setQueryFailed(true);
    } finally {
      setAiSearchLoading(false);
    }
  };

  // AI Semantic Search handler — plain words. Query syntax goes to the
  // grammar instead; `hasQuerySyntax` is the same rule the web library uses.
  const handleAiSearch = async () => {
    if (!searchQuery.trim()) {
      setIsAiSearch(false);
      setAiSearchResults([]);
      return;
    }
    if (hasQuerySyntax(searchQuery)) {
      await handleQuerySearch();
      return;
    }
    setIsQuerySearch(false);

    try {
      setAiSearchLoading(true);
      setIsAiSearch(true);
      const results = await aiService.semanticSearch(searchQuery, 20);
      setAiSearchResults(results);
      logger.info('[Streams] AI search completed', { query: searchQuery, resultCount: results.length });
    } catch (err) {
      logger.error('[Streams] AI search failed', { error: err });
      // Fall back to regular search
      setIsAiSearch(false);
      setAiSearchResults([]);
    } finally {
      setAiSearchLoading(false);
    }
  };

  // Clear AI search and return to regular view
  const clearAiSearch = () => {
    setIsAiSearch(false);
    setAiSearchResults([]);
    setIsQuerySearch(false);
    setQueryAnswer(null);
    setQueryFailed(false);
    setSearchQuery('');
  };

  const genres = ['all', ...getUniqueGenres(streams)];

  const filteredStreams = streams.filter(stream => {
    const matchesFilter = filter === 'all' || normalizeGenre(stream.genre) === filter;
    // Query syntax is not a substring of any show name; it runs on Enter.
    const matchesSearch = hasQuerySyntax(searchQuery) ||
                          stream.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          stream.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Organize streams into sections
  const organizedSections = React.useMemo(() => {
    // Most played streams
    const mostPlayed = [...streams]
      .filter(s => s.playCount && s.playCount > 0)
      .sort((a, b) => (b.playCount || 0) - (a.playCount || 0))
      .slice(0, 10);

    // Recent/New streams (by createdAt or just first 10)
    const recent = streams.slice(0, 10);

    // Group by normalized genre
    const genreMap = new Map<string, Stream[]>();
    streams.forEach(stream => {
      const genre = normalizeGenre(stream.genre);
      if (!genreMap.has(genre)) {
        genreMap.set(genre, []);
      }
      genreMap.get(genre)!.push(stream);
    });

    // Convert to array and sort by count
    const byGenre = Array.from(genreMap.entries())
      .map(([genre, items]) => ({ genre, streams: items.slice(0, 12) }))
      .filter(section => section.streams.length >= 2)
      .sort((a, b) => b.streams.length - a.streams.length);

    return { mostPlayed, recent, byGenre };
  }, [streams]);

  // Render a single stream card (reusable)
  const renderStreamCard = (stream: Stream, small = false) => (
    <Link
      key={stream.streamId}
      to={`/streams/${stream.streamId}`}
      className={`stream-card ${small ? 'stream-card-small' : ''}`}
    >
      <div className="stream-cover-wrapper">
        {stream.coverImage ? (
          <img src={stream.coverImage} alt={stream.name} className="stream-cover" />
        ) : (
          <div className="stream-cover-placeholder">
            <svg width="32" height="32" viewBox="0 0 64 64" fill="none">
              <path d="M26 24L40 32L26 40V24Z" fill="currentColor"/>
            </svg>
          </div>
        )}
        <button
          className="stream-play-overlay"
          onClick={(e) => handlePlayStream(e, stream)}
          title="Play"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7L8 5z"/>
          </svg>
        </button>
      </div>
      <div className="stream-info">
        <h3 className="stream-name">{stream.name}</h3>
        <p className="stream-genre">{normalizeGenre(stream.genre)}</p>
        {stream.uploaderDisplayName && (
          <span className="stream-artist-name">{stream.uploaderDisplayName}</span>
        )}
      </div>
    </Link>
  );

  // One row of a query answer: a show card, or a set track that opens its show.
  const renderQueryItem = (item: RadioQueryItem) =>
    item.kind === 'show' ? (
      <Link key={item.streamId} to={`/streams/${item.streamId}`} className="stream-card ai-result-card">
        <div className="stream-cover-wrapper">
          {item.coverImage ? (
            <img src={item.coverImage} alt={item.title} className="stream-cover" />
          ) : (
            <div className="stream-cover-placeholder">
              <svg width="32" height="32" viewBox="0 0 64 64" fill="none">
                <path d="M26 24L40 32L26 40V24Z" fill="currentColor"/>
              </svg>
            </div>
          )}
        </div>
        <div className="stream-info">
          <h3 className="stream-name">{item.title}</h3>
          {taggedGenre(item.genre) && <p className="stream-genre">{normalizeGenre(item.genre)}</p>}
          {item.artist && <p className="stream-artist">{item.artist}</p>}
        </div>
      </Link>
    ) : (
      <Link
        key={`${item.setId}/${item.segmentId}`}
        to={`/streams/${item.streamId}`}
        className="query-track"
      >
        <span className="query-track-title">{item.title || 'Unidentified track'}</span>
        {item.artist && <span className="query-track-artist">{item.artist}</span>}
        <span className="query-track-where">
          in {item.streamName}
          {typeof item.startTimeMs === 'number' && ` at ${formatStart(item.startTimeMs)}`}
        </span>
      </Link>
    );

  // Calculate top users based on stream count
  const topUsers: TopUser[] = React.useMemo(() => {
    const userMap = new Map<string, TopUser>();

    streams.forEach(stream => {
      if (stream.uploaderUsername) {
        const existing = userMap.get(stream.uploaderUsername);
        if (existing) {
          existing.streamCount++;
        } else {
          userMap.set(stream.uploaderUsername, {
            username: stream.uploaderUsername,
            displayName: stream.uploaderDisplayName || stream.uploaderUsername,
            profilePictureUrl: stream.uploaderProfilePictureUrl,
            streamCount: 1,
          });
        }
      }
    });

    return Array.from(userMap.values())
      .sort((a, b) => b.streamCount - a.streamCount)
      .slice(0, 10); // Top 10 users
  }, [streams]);

  if (loading) {
    return (
      <div className="streams-loading">
        <div className="loading-spinner">
          <div className="spinner-circle"></div>
          <div className="spinner-circle"></div>
          <div className="spinner-circle"></div>
        </div>
        <p>Loading streams...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="streams-error container">
        <div className="error-card">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" className="error-icon">
            <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="2"/>
            <path d="M32 20V36" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="32" cy="44" r="2" fill="currentColor"/>
          </svg>
          <h2>Oops! Something went wrong</h2>
          <p>{error}</p>
          <button onClick={loadStreams} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="streams-page">
      <div className="streams-controls container">
        <div className="search-bar-wrapper">
          <div className="search-bar-container">
            <div className="search-bar">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="search-icon">
                <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2"/>
                <path d="M13 13L17 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <input
                type="text"
                placeholder="Try 'chill jazz vibes' or 'upbeat electronic for a party'..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (!e.target.value.trim()) {
                    setIsAiSearch(false);
                    setAiSearchResults([]);
                    setIsQuerySearch(false);
                    setQueryAnswer(null);
                    setQueryFailed(false);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchQuery.trim()) {
                    handleAiSearch();
                  }
                }}
                className="search-input"
              />
              {searchQuery && (
                <button
                  className="search-clear-btn"
                  onClick={clearAiSearch}
                  title="Clear search"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
            </div>
            <button
              className={`ai-search-btn ${aiSearchLoading ? 'loading' : ''}`}
              onClick={handleAiSearch}
              disabled={!searchQuery.trim() || aiSearchLoading}
              title="AI Semantic Search"
            >
              {aiSearchLoading ? (
                <div className="btn-spinner"></div>
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 2L12.5 7L18 7.5L14 11.5L15 17L10 14L5 17L6 11.5L2 7.5L7.5 7L10 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                </svg>
              )}
              <span>AI Search</span>
            </button>
          </div>
          <p className="ai-search-hint">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
              <path d="M10 2L12.5 7L18 7.5L14 11.5L15 17L10 14L5 17L6 11.5L2 7.5L7.5 7L10 2Z" fill="currentColor"/>
            </svg>
            Powered by AI — describe what you want to hear in natural language
          </p>
        </div>

        {/* Combined Sort/View Dropdown */}
        <div className="sort-dropdown">
          <label htmlFor="sortBy" className="sort-label">View:</label>
          <select
            id="sortBy"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'trending' | 'most-played' | 'top-creators')}
            className="sort-select"
          >
            <option value="trending">Trending</option>
            <option value="most-played">Most Played</option>
            <option value="top-creators">Top Creators</option>
          </select>
        </div>

        <div className="genre-filters">
          {genres.map((genre) => (
            <button
              key={genre}
              onClick={() => setFilter(genre)}
              className={`genre-filter ${filter === genre ? 'active' : ''}`}
            >
              {genre === 'all' ? 'All' : genre}
            </button>
          ))}
        </div>
      </div>

      <div className="streams-content container">
        {/* Query results — the shared grammar over Radio's shows (TEM-1529) */}
        {isAiSearch && isQuerySearch && (
          <div className="ai-search-results query-results">
            <div className="ai-results-header">
              <div className="ai-results-title">
                <span>Query results for "{searchQuery}"</span>
              </div>
              <button className="ai-results-clear" onClick={clearAiSearch}>
                Clear Search
              </button>
            </div>
            {queryFailed && <p className="query-error">{QUERY_FAILED}</p>}
            {queryAnswer && (
              <>
                {queryAnswer.rows !== null && (
                  <p className="query-summary">
                    {describeQueryAnswer(queryAnswer)}
                    {queryAnswer.query !== searchQuery.trim() && (
                      <> · ran as <code>{queryAnswer.query}</code></>
                    )}
                  </p>
                )}
                {queryAnswer.error && queryAnswer.error.kind !== 'field_unavailable' && (
                  <p className="query-error">{queryAnswer.error.message}</p>
                )}
                {queryAnswer.notes.length > 0 && (
                  <ul className="query-notes">
                    {queryAnswer.notes.map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                )}
                {queryAnswer.items.length > 0 && (
                  <div className={queryAnswer.rows === 'tracks' ? 'query-track-list' : 'streams-grid ai-results-grid'}>
                    {queryAnswer.items.map((item) => renderQueryItem(item))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* AI Search Results */}
        {isAiSearch && !isQuerySearch && (
          <div className="ai-search-results">
            <div className="ai-results-header">
              <div className="ai-results-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L14.5 8L21 8.5L16 13L17.5 20L12 16.5L6.5 20L8 13L3 8.5L9.5 8L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                </svg>
                <span>AI Search Results for "{searchQuery}"</span>
              </div>
              <button className="ai-results-clear" onClick={clearAiSearch}>
                Clear AI Search
              </button>
            </div>
            {aiSearchResults.length === 0 ? (
              <div className="no-streams">
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none" className="no-streams-icon">
                  <circle cx="40" cy="40" r="30" stroke="currentColor" strokeWidth="2" opacity="0.3"/>
                  <path d="M40 25V40L50 50" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <h3>No matches found</h3>
                <p>Try different keywords or phrases</p>
              </div>
            ) : (
              <div className="streams-grid ai-results-grid">
                {aiSearchResults.map((result) => (
                  <Link
                    key={result.id}
                    to={`/streams/${result.payload.stream_id || result.id}`}
                    className="stream-card ai-result-card"
                  >
                    <div className="stream-card-background">
                      <div className="wave-decoration"></div>
                    </div>
                    <div className="ai-match-score">
                      {Math.round(result.score * 100)}% match
                    </div>
                    <div className="stream-cover-wrapper">
                      {result.payload.cover_image ? (
                        <img src={result.payload.cover_image} alt={result.payload.title || 'Stream'} className="stream-cover" />
                      ) : (
                        <div className="stream-cover-placeholder">
                          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                            <circle cx="32" cy="32" r="24" stroke="currentColor" strokeWidth="2"/>
                            <path d="M26 24L40 32L26 40V24Z" fill="currentColor"/>
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="stream-info">
                      <h3 className="stream-name">{result.payload.title || 'Untitled'}</h3>
                      <p className="stream-genre">{normalizeGenre(result.payload.genre)}</p>
                      {result.payload.description && (
                        <p className="stream-description">{result.payload.description}</p>
                      )}
                      {result.payload.artist && (
                        <p className="stream-artist">{result.payload.artist}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Regular Streams View */}
        {!isAiSearch && sortBy === 'top-creators' ? (
          // Show Top Creators List
          topUsers.length === 0 ? (
            <div className="no-streams">
              <svg width="120" height="120" viewBox="0 0 120 120" fill="none" className="no-streams-icon">
                <circle cx="60" cy="60" r="50" stroke="currentColor" strokeWidth="2" opacity="0.2"/>
                <circle cx="60" cy="60" r="35" stroke="currentColor" strokeWidth="2" opacity="0.3"/>
                <circle cx="60" cy="60" r="20" stroke="currentColor" strokeWidth="2" opacity="0.5"/>
              </svg>
              <h3>No creators found</h3>
              <p>No active stream creators yet</p>
            </div>
          ) : (
            <div className="top-creators-grid">
              <div className="top-creators-header">
                <h2>Top Creators</h2>
                <p>Most active users by stream count</p>
              </div>
              <div className="top-creators-list">
                {topUsers.map((user, index) => (
                  <Link
                    key={user.username}
                    to={`/users/${user.username}`}
                    className="top-creator-card"
                  >
                    <div className="creator-rank">#{index + 1}</div>
                    {user.profilePictureUrl ? (
                      <img
                        src={user.profilePictureUrl}
                        alt={user.displayName}
                        className="creator-avatar"
                      />
                    ) : (
                      <div className="creator-avatar-placeholder">
                        {user.displayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="creator-info">
                      <h3 className="creator-name">{user.displayName}</h3>
                      <p className="creator-username">@{user.username}</p>
                      <p className="creator-stats">
                        {user.streamCount} {user.streamCount === 1 ? 'stream' : 'streams'}
                      </p>
                    </div>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="creator-arrow">
                      <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Link>
                ))}
              </div>
            </div>
          )
        ) : !isAiSearch && filteredStreams.length === 0 ? (
          <div className="no-streams">
            <svg width="120" height="120" viewBox="0 0 120 120" fill="none" className="no-streams-icon">
              <circle cx="60" cy="60" r="50" stroke="currentColor" strokeWidth="2" opacity="0.2"/>
              <circle cx="60" cy="60" r="35" stroke="currentColor" strokeWidth="2" opacity="0.3"/>
              <circle cx="60" cy="60" r="20" stroke="currentColor" strokeWidth="2" opacity="0.5"/>
            </svg>
            <h3>No streams found</h3>
            <p>Try adjusting your filters or search query</p>
          </div>
        ) : !isAiSearch && filter === 'all' && !searchQuery ? (
          /* Organized sections view - when no filter/search active */
          <div className="streams-sections">
            {/* Most Played Section */}
            {organizedSections.mostPlayed.length > 0 && (
              <div className="stream-section">
                <div className="section-header">
                  <h2 className="section-title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeLinejoin="round" strokeLinecap="round"/>
                    </svg>
                    Most Played
                  </h2>
                  <span className="section-count">{organizedSections.mostPlayed.length}</span>
                </div>
                <div className="section-scroll">
                  {organizedSections.mostPlayed.map(stream => renderStreamCard(stream, true))}
                </div>
              </div>
            )}

            {/* Genre Sections */}
            {organizedSections.byGenre.map(section => (
              <div key={section.genre} className="stream-section">
                <div className="section-header">
                  <h2 className="section-title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 6v6l4 2"/>
                    </svg>
                    {section.genre}
                  </h2>
                  <span className="section-count">{section.streams.length}</span>
                </div>
                <div className="section-scroll">
                  {section.streams.map(stream => renderStreamCard(stream, true))}
                </div>
              </div>
            ))}
          </div>
        ) : !isAiSearch ? (
          /* Filtered/search grid view */
          <div className="streams-grid">
            {filteredStreams.map(stream => renderStreamCard(stream))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Streams;
